/**
 * Screen Wake Lock & iOS Keep-Awake Engine
 *
 * Garante que a tela NUNCA apague ou escureça em NENHUM modelo de celular, tablet ou PC:
 * 1. W3C Screen Wake Lock API nativo (iOS 16.4+, iOS 17+, iOS 18+, Android Chrome, Edge, Safari):
 *    - Captura síncrona imediata em qualquer toque/clique do usuário no app.
 *    - Re-aquisição automática imediata em release, visibilitychange, focus e a cada ciclo de reprodução.
 * 2. iOS CoreMedia Keep-Alive Video (In-Viewport Micro-Video):
 *    - Mantido estritamente dentro do viewport (1x1px no canto inferior ou 100x100px no canto superior, opacidade mínima)
 *      para que o algoritmo de economia de energia do WebKit NUNCA suspenda o motor de vídeo nem considere offscreen.
 *    - Suporte a MP4 H.264 e WebM com loops contínuos e auto-retomada em pause/ended/stalled.
 * 3. iOS WebAudio Session Keep-Alive:
 *    - Mantém uma sessão de áudio inaudível no CoreAudio do iOS para que o sistema reconheça o app
 *      como um processo de mídia em primeiro plano ativo.
 * 4. AndroidBridge nativo (App APK / TV Box):
 *    - setKeepScreenOn(true/false) na MainActivity nativa.
 */

import { useEffect, useRef, useState } from 'react';

// Vídeo MP4 de 1 frame em base64 (H.264) compatível com todas as versões do WebKit / AVPlayer da Apple
const IOS_NO_SLEEP_VIDEO_SRC =
  'data:video/mp4;base64,AAAAHGZ0eXBNNFYgAAACAGlzb21pc28yYXZjMQAAAAhmcmVlAAAGF21kYXTeBAAAbGliZmFhYyAxLjI4AABCAJMgBDIARwAAArEGBf//rdxF6b3m2Ui3lizYINkj7u94MjY0IC0gY29yZSAxNDIgcjIgOTU2YzhkOCAtIEguMjY0L01QRUctNCBBVkMgY29kZWMgLSBDb3B5bGVmdCAyMDAzLTIwMTQgLSBodHRwOi8vd3d3LnZpZGVvbGFuLm9yZy94MjY0Lmh0bWwgLSBvcHRpb25zOiBjYWJhYz0wIHJlZj0zIGRlYmxvY2s9MTowOjAgYW5hbHlzZT0weDE6MHgxMTEgbWU9aGV4IHN1Ym1lPTcgcHN5PTEgcHN5X3JkPTEuMDA6MC4wMCBtaXhlZF9yZWY9MSBtZV9yYW5nZT0xNiBjaHJvbWFfbWU9MSB0cmVsbGlzPTEgOHg4ZGN0PTAgY3FtPTAgZGVhZHpvbmU9MjEsMTEgZmFzdF9wc2tpcD0xIGNocm9tYV9xcF9vZmZzZXQ9LTIgdGhyZWFkcz02IGxvb2thaGVhZF90aHJlYWRzPTEgc2xpY2VkX3RocmVhZHM9MCBucj0wIGRlY2ltYXRlPTEgaW50ZXJsYWNlZD0wIGJsdXJheV9jb21wYXQ9MCBjb25zdHJhaW5lZF9pbnRyYT0wIGJmcmFtZXM9MCB3ZWlnaHRwPTAga2V5aW50PTI1MCBrZXlpbnRfbWluPTI1IHNjZW5lY3V0PTQwIGludHJhX3JlZnJlc2g9MCByY19sb29rYWhlYWQ9NDAgcmM9Y3JmIG1idHJlZT0xIGNyZj0yMy4wIHFjb21wPTAuNjAgcXBtaW49MCBxcG1heD02OSBxcHN0ZXA9NCB2YnZfbWF4cmF0ZT03NjggdmJ2X2J1ZnNpemU9MzAwMCBjcmZfbWF4PTAuMCBuYWxfaHJkPW5vbmUgZmlsbGVyPTAgaXBfcmF0aW89MS40MCBhcT0xOjEuMDAAgAAAAFZliIQL8mKAAKvMnJycnJycnJycnXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXiEASZACGQAjgCEASZACGQAjgAAAAAdBmjgX4GSAIQBJkAIZACOAAAAAB0GaVAX4GSAhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZpgL8DJIQBJkAIZACOAIQBJkAIZACOAAAAABkGagC/AySEASZACGQAjgAAAAAZBmqAvwMkhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZrAL8DJIQBJkAIZACOAAAAABkGa4C/AySEASZACGQAjgCEASZACGQAjgAAAAAZBmwAvwMkhAEmQAhkAI4AAAAAGQZsgL8DJIQBJkAIZACOAIQBJkAIZACOAAAAABkGbQC/AySEASZACGQAjgCEASZACGQAjgAAAAAZBm2AvwMkhAEmQAhkAI4AAAAAGQZuAL8DJIQBJkAIZACOAIQBJkAIZACOAAAAABkGboC/AySEASZACGQAjgAAAAAZBm8AvwMkhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZvgL8DJIQBJkAIZACOAAAAABkGaAC/AySEASZACGQAjgCEASZACGQAjgAAAAAZBmiAvwMkhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZpAL8DJIQBJkAIZACOAAAAABkGaYC/AySEASZACGQAjgCEASZACGQAjgAAAAAZBmoAvwMkhAEmQAhkAI4AAAAAGQZqgL8DJIQBJkAIZACOAIQBJkAIZACOAAAAABkGawC/AySEASZACGQAjgAAAAAZBmuAvwMkhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZsAL8DJIQBJkAIZACOAAAAABkGbIC/AySEASZACGQAjgCEASZACGQAjgAAAAAZBm0AvwMkhAEmQAhkAI4AhAEmQAhkAI4AAAAAGQZtgL8DJIQBJkAIZACOAAAAABkGbgCvAySEASZACGQAjgCEASZACGQAjgAAAAAZBm6AnwMkhAEmQAhkAI4AhAEmQAhkAI4AhAEmQAhkAI4AhAEmQAhkAI4AAAAhubW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAABDcAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwAAAzB0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAABAAAAAAAAA+kAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAALAAAACQAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAPpAAAAAAABAAAAAAKobWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAB1MAAAdU5VxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAACU21pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAhNzdGJsAAAAr3N0c2QAAAAAAAAAAQAAAJ9hdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAALAAkABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAALWF2Y0MBQsAN/+EAFWdCwA3ZAsTsBEAAAPpAADqYA8UKkgEABWjLg8sgAAAAHHV1aWRraEDyXyRPxbo5pRvPAyPzAAAAAAAAABhzdHRzAAAAAAAAAAEAAAAeAAAD6QAAABRzdHNzAAAAAAAAAAEAAAABAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAABAAAAAQAAAIxzdHN6AAAAAAAAAAAAAAAeAAADDwAAAAsAAAALAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAACgAAAAoAAAAKAAAAiHN0Y28AAAAAAAAAHgAAAEYAAANnAAADewAAA5gAAAO0AAADxwAAA+MAAAP2AAAEEgAABCUAAARBAAAEXQAABHAAAASMAAAEnwAABLsAAATOAAAE6gAABQYAAAUZAAAFNQAABUgAAAVkAAAFdwAABZMAAAWmAAAFwgAABd4AAAXxAAAGDQAABGh0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAACAAAAAAAABDcAAAAAAAAAAAAAAAEBAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAkZWR0cwAAABxlbHN0AAAAAAAAAAEAAAQkAAADcAABAAAAAAPgbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAC7gAAAykBVxAAAAAAALWhkbHIAAAAAAAAAAHNvdW4AAAAAAAAAAAAAAABTb3VuZEhhbmRsZXIAAAADi21pbmYAAAAQc21oZAAAAAAAAAAAAAAAJGRpbmYAAAAcZHJlZgAAAAAAAAABAAAADHVybCAAAAABAAADT3N0YmwAAABnc3RzZAAAAAAAAAABAAAAV21wNGEAAAAAAAAAAQAAAAAAAAAAAAIAEAAAAAC7gAAAAAAAM2VzZHMAAAAAA4CAgCIAAgAEgICAFEAVBbjYAAu4AAAADcoFgICAAhGQBoCAgAECAAAAIHN0dHMAAAAAAAAAAgAAADIAAAQAAAAAAQAAAkAAAAFUc3RzYwAAAAAAAAAbAAAAAQAAAAEAAAABAAAAAgAAAAIAAAABAAAAAwAAAAEAAAABAAAABAAAAAIAAAABAAAABgAAAAEAAAABAAAABwAAAAIAAAABAAAACAAAAAEAAAABAAAACQAAAAIAAAABAAAACgAAAAEAAAABAAAACwAAAAIAAAABAAAADQAAAAEAAAABAAAADgAAAAIAAAABAAAADwAAAAEAAAABAAAAEAAAAAIAAAABAAAAEQAAAAEAAAABAAAAEgAAAAIAAAABAAAAFAAAAAEAAAABAAAAFQAAAAIAAAABAAAAFgAAAAEAAAABAAAAFwAAAAIAAAABAAAAGAAAAAEAAAABAAAAGQAAAAIAAAABAAAAGgAAAAEAAAABAAAAGwAAAAIAAAABAAAAHQAAAAEAAAABAAAAHgAAAAIAAAABAAAAHwAAAAQAAAABAAAA4HN0c3oAAAAAAAAAAAAAADMAAAAaAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAAAJAAAACQAAAAkAAACMc3RjbwAAAAAAAAAfAAAALAAAA1UAAANyAAADhgAAA6IAAAO+AAAD0QAAA+0AAAQAAAAEHAAABC8AAARLAAAEZwAABHoAAASWAAAEqQAABMUAAATYAAAE9AAABRAAAAUjAAAFPwAABVIAAAVuAAAFgQAABZ0AAAWwAAAFzAAABegAAAX7AAAGFwAAAGJ1ZHRhAAAAWm1ldGEAAAAAAAAAIWhkbHIAAAAAAAAAAG1kaXJhcHBsAAAAAAAAAAAAAAAALWlsc3QAAAAlqXRvbwAAAB1kYXRhAAAAAQAAAABMYXZmNTUuMzMuMTAw';

let isWakeLockActive = false;
let nativeWakeLockInstance: any = null;
let isRequestingNativeWakeLock = false;
let lastWakeLockError = 'nenhum';
let wakeLockSupported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
let keepAliveVideoElement: HTMLVideoElement | null = null;
let audioContext: AudioContext | null = null;
let audioOscillator: OscillatorNode | null = null;
let watchdogInterval: any = null;
let reacquireTimeout: any = null;
let currentStatus = 'NÃO_INICIADO';
const statusListeners = new Set<(status: string) => void>();

function updateStatus(status: string) {
  currentStatus = status;
  statusListeners.forEach((listener) => {
    try {
      listener(status);
    } catch {}
  });
}

function isIosPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function hasAndroidBridge(): boolean {
  return typeof window !== 'undefined' && Boolean((window as any).AndroidBridge?.setKeepScreenOn);
}

function getCalculatedStatus(): string {
  if (!isWakeLockActive) return 'INATIVO';
  const nativeOk = Boolean(nativeWakeLockInstance && !nativeWakeLockInstance.released);
  const videoOk = Boolean(keepAliveVideoElement && !keepAliveVideoElement.paused);
  const audioOk = Boolean(audioContext && audioContext.state === 'running');
  const androidOk = Boolean(hasAndroidBridge());

  if (androidOk) return '✅ ATIVO (ANDROID NATIVO FLAG_KEEP_SCREEN_ON)';
  if (nativeOk) return '✅ ATIVO (NATIVO W3C)';
  if (videoOk && audioOk) return '✅ ATIVO (VÍDEO + ÁUDIO COREMEDIA IOS)';
  if (videoOk) return '✅ ATIVO (VÍDEO VIEWPORT IOS)';
  if (audioOk) return '✅ ATIVO (ÁUDIO COREMEDIA IOS)';
  return `⚠️ INATIVO (W3C: ${lastWakeLockError || 'aguardando toque'})`;
}

/**
 * Cria e mantém um AudioContext inaudível para registrar uma sessão ativa de mídia no iOS CoreAudio
 * NOTA: EXCLUSIVO iOS! No Android Stick, NUNCA rodar para evitar estalos de áudio e Mojo crashes.
 */
function ensureIosAudioKeepAlive() {
  if (typeof window === 'undefined') return;
  if (hasAndroidBridge() || !isIosPlatform()) {
    cleanupIosAudioKeepAlive();
    return;
  }
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    if (!audioContext || audioContext.state === 'closed') {
      audioContext = new AudioCtx();
    }

    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }

    if (!audioOscillator && audioContext.state === 'running') {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      gain.gain.value = 0.00001; // Praticamente mudo/inaudível
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.start();
      audioOscillator = osc;
    }
  } catch (err: any) {
    // Ignora silenciosamente se o navegador rejeitar autoplay de áudio
  }
}

function cleanupIosAudioKeepAlive() {
  if (audioOscillator) {
    try {
      audioOscillator.stop();
      audioOscillator.disconnect();
    } catch {}
    audioOscillator = null;
  }
  if (audioContext && audioContext.state !== 'closed') {
    try {
      audioContext.close().catch(() => {});
    } catch {}
    audioContext = null;
  }
}

/**
 * Cria ou reativa o micro-vídeo mudo DENTRO DO VIEWPORT para reforçar a prevenção de sono no iOS.
 * NOTA CRÍTICA DE ARQUITETURA:
 * 1. O vídeo NUNCA pode ter z-index negativo (-1), pois o LivePlayer usa `fixed inset-0 z-50 bg-black`.
 *    Se ficar atrás de um fundo preto opaco, o WebKit aplica "Occlusion Culling", considerando o vídeo
 *    100% ocluído e suspendendo a asserção de manter a tela acesa. Ele deve ficar em z-index 99999 com pointerEvents: none.
 * 2. O atributo `loop` NUNCA deve ser usado, pois o WebKit classifica vídeos com loop como decorativos/ambiente,
 *    isentando-os de manter a tela acesa. Usamos reinício via eventos `ended` e `timeupdate`.
 */
function ensureIosKeepAliveVideo() {
  if (typeof document === 'undefined') return;
  // EXCLUSIVO iOS: No Android Stick com AndroidBridge ou fora do iOS, NUNCA criar vídeo em background
  if (hasAndroidBridge() || !isIosPlatform()) {
    cleanupIosKeepAliveVideo();
    return;
  }

  try {
    if (!keepAliveVideoElement) {
      const v = document.createElement('video');
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.setAttribute('muted', '');
      v.setAttribute('loop', '');
      v.setAttribute('disablePictureInPicture', '');
      v.setAttribute('aria-hidden', 'true');
      v.playsInline = true;
      v.muted = true;
      (v as any).defaultMuted = true;
      v.loop = true;
      v.autoplay = true;
      v.preload = 'auto';

      // 1. Arquivo estático real no mesmo domínio (/keep-awake.mp4)
      // 2. Fallback Data URI MP4 H.264
      const srcStatic = document.createElement('source');
      srcStatic.src = '/keep-awake.mp4';
      srcStatic.type = 'video/mp4';
      v.appendChild(srcStatic);

      const srcB64 = document.createElement('source');
      srcB64.src = IOS_NO_SLEEP_VIDEO_SRC;
      srcB64.type = 'video/mp4';
      v.appendChild(srcB64);

      v.src = '/keep-awake.mp4';

      // Estilo: Em primeiro plano (z-index 99999) para evitar Occlusion Culling do WebKit,
      // com opacidade mínima (0.01) e pointerEvents: none para não interferir na interface.
      v.style.position = 'fixed';
      v.style.left = '0px';
      v.style.top = '0px';
      v.style.width = '100px';
      v.style.height = '100px';
      v.style.opacity = '0.01';
      v.style.pointerEvents = 'none';
      v.style.zIndex = '99999';

      // Reinicia o tempo para manter o pipeline ativo
      v.addEventListener('timeupdate', () => {
        if (v.currentTime > 0.5) {
          v.currentTime = Math.random() * 0.3;
        }
      });

      // Se o vídeo terminar ou pausar, retoma imediatamente
      v.addEventListener('ended', () => {
        if (isWakeLockActive) {
          v.currentTime = 0;
          v.play().catch(() => {});
        }
      });

      v.addEventListener('pause', () => {
        if (isWakeLockActive) {
          v.play().catch(() => {});
        }
      });

      v.addEventListener('stalled', () => {
        if (isWakeLockActive) {
          v.currentTime = 0;
          v.play().catch(() => {});
        }
      });

      v.addEventListener('error', () => {
        if (v.src !== IOS_NO_SLEEP_VIDEO_SRC) {
          v.src = IOS_NO_SLEEP_VIDEO_SRC;
          v.play().catch(() => {});
        }
      });

      document.body.appendChild(v);
      keepAliveVideoElement = v;
    }

    if (keepAliveVideoElement && keepAliveVideoElement.paused) {
      const p = keepAliveVideoElement.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {});
      }
    }
  } catch (err: any) {
    console.debug('[WakeLock] Erro ao iniciar vídeo keep-alive:', err?.message);
  }
}


/**
 * Remove o micro-vídeo mudo do DOM para permitir re-criação limpa com autoplay autorizado pelo WebKit
 */
function cleanupIosKeepAliveVideo() {
  if (keepAliveVideoElement) {
    try {
      keepAliveVideoElement.pause();
      keepAliveVideoElement.removeAttribute('src');
      keepAliveVideoElement.load();
      if (keepAliveVideoElement.parentNode) {
        keepAliveVideoElement.parentNode.removeChild(keepAliveVideoElement);
      }
    } catch {}
    keepAliveVideoElement = null;
  }
}

/**
 * Tenta adquirir a API nativa W3C navigator.wakeLock
 * Executa dentro do contexto de ativação transiente do Safari/WebKit.
 */
export async function requestNativeWakeLock(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) {
    wakeLockSupported = false;
    lastWakeLockError = 'API wakeLock não disponível no navigator';
    return false;
  }

  wakeLockSupported = true;

  if (nativeWakeLockInstance && !nativeWakeLockInstance.released) {
    return true;
  }

  if (isRequestingNativeWakeLock) {
    return false;
  }

  isRequestingNativeWakeLock = true;

  try {
    const lock = await (navigator as any).wakeLock.request('screen');
    nativeWakeLockInstance = lock;
    lastWakeLockError = 'nenhum';

    lock.addEventListener('release', () => {
      nativeWakeLockInstance = null;
      updateStatus(getCalculatedStatus());
    });

    updateStatus(getCalculatedStatus());
    return true;
  } catch (err: any) {
    lastWakeLockError = `${err?.name || 'Error'}: ${err?.message || String(err)}`;
    console.warn('[WakeLock] Erro ao solicitar wakeLock nativo:', lastWakeLockError);
    updateStatus(getCalculatedStatus());
    return false;
  } finally {
    isRequestingNativeWakeLock = false;
  }
}

/**
 * Ativa o modo permanente de tela acesa em todas as camadas disponíveis
 */
export async function enableScreenWakeLock(): Promise<void> {
  isWakeLockActive = true;

  // 1. PRIORIDADE MÁXIMA: W3C navigator.wakeLock nativo IMEDIATAMENTE
  // Deve ser a PRIMEIRA chamada dentro do callstack do gesto do usuário,
  // antes de qualquer manipulação de DOM ou áudio que possa consumir o token de ativação transiente no WebKit.
  await requestNativeWakeLock();

  // 2. Android Bridge nativo (APK)
  if (hasAndroidBridge()) {
    try {
      (window as any).AndroidBridge.setKeepScreenOn(true);
    } catch {}
  }

  // 3. Camadas de reforço de mídia EXCLUSIVAS para iOS (Safari/WebKit)
  // No Android/Stick, criar <video> e AudioContext causa sobrecarga no AudioFlinger e Mojo Crash
  if (isIosPlatform() && !hasAndroidBridge()) {
    ensureIosKeepAliveVideo();
    ensureIosAudioKeepAlive();
  } else {
    cleanupIosKeepAliveVideo();
    cleanupIosAudioKeepAlive();
  }

  updateStatus(getCalculatedStatus());

  // 5. Watchdog periódico (a cada 8 segundos): garante que nenhuma camada caiu
  if (!watchdogInterval) {
    watchdogInterval = setInterval(() => {
      if (!isWakeLockActive) return;

      if (isIosPlatform() && !hasAndroidBridge()) {
        if (!keepAliveVideoElement || keepAliveVideoElement.paused) {
          ensureIosKeepAliveVideo();
        }
        if (audioContext && audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {});
        }
      }

      if (
        typeof navigator !== 'undefined' &&
        'wakeLock' in navigator &&
        (!nativeWakeLockInstance || nativeWakeLockInstance.released)
      ) {
        requestNativeWakeLock();
      }

      if (hasAndroidBridge()) {
        try {
          (window as any).AndroidBridge.setKeepScreenOn(true);
        } catch {}
      }

      updateStatus(getCalculatedStatus());
    }, 8000);
  }
}

/**
 * Desativa e libera a tela para o comportamento normal do sistema
 */
export function disableScreenWakeLock(): void {
  isWakeLockActive = false;

  if (watchdogInterval) {
    clearInterval(watchdogInterval);
    watchdogInterval = null;
  }
  if (reacquireTimeout) {
    clearTimeout(reacquireTimeout);
    reacquireTimeout = null;
  }

  // 1. Android Bridge
  if (typeof window !== 'undefined' && (window as any).AndroidBridge?.setKeepScreenOn) {
    try {
      (window as any).AndroidBridge.setKeepScreenOn(false);
    } catch {}
  }

  // 2. Micro-vídeo iOS
  cleanupIosKeepAliveVideo();

  // 3. WebAudio Keep-Alive iOS
  cleanupIosAudioKeepAlive();

  // 4. W3C WakeLock
  if (nativeWakeLockInstance) {
    try {
      nativeWakeLockInstance.release();
    } catch {}
    nativeWakeLockInstance = null;
  }

  updateStatus('LIBERADO');
}

/**
 * Retorna o status atual do WakeLock para telemetria/diagnóstico
 */
export function getScreenWakeLockStatus(): string {
  if (!isWakeLockActive) return 'INATIVO';
  return getCalculatedStatus();
}

/**
 * Retorna telemetria detalhada para a tela de diagnóstico do player
 */
export function getDetailedWakeLockStatus(): {
  active: boolean;
  nativeWakeLock: boolean;
  wakeLockSupported: boolean;
  lastWakeLockError: string;
  iosVideo: boolean;
  iosAudio: boolean;
  androidBridge: boolean;
  statusText: string;
} {
  const nativeOk = Boolean(nativeWakeLockInstance && !nativeWakeLockInstance.released);
  const videoOk = Boolean(keepAliveVideoElement && !keepAliveVideoElement.paused);
  const audioOk = Boolean(audioContext && audioContext.state === 'running');
  const androidOk = Boolean(typeof window !== 'undefined' && (window as any).AndroidBridge?.setKeepScreenOn);

  const errorMsg = !wakeLockSupported
    ? 'Não disponível no WebKit PWA Standalone (ativo via Vídeo + Áudio CoreMedia)'
    : lastWakeLockError;

  return {
    active: isWakeLockActive,
    nativeWakeLock: nativeOk,
    wakeLockSupported,
    lastWakeLockError: errorMsg,
    iosVideo: videoOk,
    iosAudio: audioOk,
    androidBridge: androidOk,
    statusText: getCalculatedStatus(),
  };
}

// ---------------------------------------------------------------------------
// CAPTURADOR GLOBAL PERMANENTE DE GESTOS DO USUÁRIO
// Captura qualquer toque na tela (mesmo em menus, cards de canais, botões do player)
// e executa a requisição nativa dentro da ativação transiente de usuário do Safari.
// Padrão WHATWG: touchend, pointerup, click CONCEDEM ativação transiente; touchstart NÃO concede.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  const onGlobalActivationGesture = () => {
    if (isWakeLockActive || typeof document !== 'undefined') {
      if (!nativeWakeLockInstance || nativeWakeLockInstance.released) {
        requestNativeWakeLock();
      }
      if (isWakeLockActive) {
        if (!keepAliveVideoElement || keepAliveVideoElement.paused) {
          ensureIosKeepAliveVideo();
        }
        ensureIosAudioKeepAlive();
      }
    }
  };

  // Fase de captura em eventos de ativação válidos
  window.addEventListener('touchend', onGlobalActivationGesture, { passive: true, capture: true });
  window.addEventListener('pointerup', onGlobalActivationGesture, { passive: true, capture: true });
  window.addEventListener('click', onGlobalActivationGesture, { passive: true, capture: true });

  // Re-aquisição automática imediata quando a aba volta a ficar visível
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && isWakeLockActive) {
        requestNativeWakeLock();
        if (!keepAliveVideoElement || keepAliveVideoElement.paused) {
          ensureIosKeepAliveVideo();
        }
        ensureIosAudioKeepAlive();
      }
    });
  }
}

/**
 * Hook do React para gerenciar automaticamente a tela acesa durante a vida de um Player
 */
export function useScreenWakeLock(isActive: boolean) {
  const [status, setStatus] = useState<string>(currentStatus);
  const activeRef = useRef(isActive);
  activeRef.current = isActive;

  useEffect(() => {
    const handleStatus = (newStatus: string) => setStatus(newStatus);
    statusListeners.add(handleStatus);
    return () => {
      statusListeners.delete(handleStatus);
    };
  }, []);

  useEffect(() => {
    if (!isActive) {
      disableScreenWakeLock();
      return;
    }

    enableScreenWakeLock();

    const onUserInteraction = () => {
      if (activeRef.current) {
        enableScreenWakeLock();
      }
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined') {
        if (document.visibilityState === 'visible' && activeRef.current) {
          enableScreenWakeLock();
        }
      }
    };

    window.addEventListener('touchend', onUserInteraction, { passive: true, capture: true });
    window.addEventListener('pointerup', onUserInteraction, { passive: true, capture: true });
    window.addEventListener('click', onUserInteraction, { passive: true, capture: true });
    window.addEventListener('focus', onUserInteraction);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('touchend', onUserInteraction, { capture: true });
      window.removeEventListener('pointerup', onUserInteraction, { capture: true });
      window.removeEventListener('click', onUserInteraction, { capture: true });
      window.removeEventListener('focus', onUserInteraction);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      disableScreenWakeLock();
    };
  }, [isActive]);

  return { status };
}
